import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { Button, getExpirationStatus, H3, List, ListItem, LoaderDots, Text } from 'components';
import ModalFactory from 'components/Modal/ModalFactory';
import { SuccessToastContainer } from 'components/Toast/ToastContainer';
import { appConfig } from 'config/app';
import { theme, toRem } from 'design';
import AccountSetupIllustration from 'images/illustrations/AddPaymentMethod.svg';
import { PaymentMethod, PaymentMethodRemove } from 'modals';
import { selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Card, deleteCardRequest, getCardRequest } from 'store/Card';
import { IStore } from 'store/types';
import { getCurrentUserRequest, selectCurrentUser, selectCurrentUserCard } from 'store/User';
import { ICardDetails, RequestStatus } from 'types';
import { formatDateForUI } from 'utils/date';
import { ASVLayout, Content, ContentContainer, Header, PaymentMethodAccountNumber, PaymentMethodActions, PaymentMethodDisclaimer } from './styles';

type ModalContentTypes = 'AddPaymentMethod' | 'RemovePaymentMethod' | undefined;

const CardExpirationDate = styled.span``;

const CardExpirationStatus = styled.span`
  color: ${theme.color.danger.medium};
  font-size: ${toRem(11)};
  margin-right: ${toRem(10)};
`;

interface PaymentMethodFormProps {
  accountSetup?: boolean;
}

const PaymentMethodForm = ({ accountSetup }: PaymentMethodFormProps) => {
  // UI State
  const dispatch = useDispatch();
  const isLoading = false;

  // Modal
  const [modalContent, setModalContent] = useState<ModalContentTypes>(undefined);
  const currentUser = useSelector(selectCurrentUser());
  const cardId = currentUser?.card?.id;
  const tokenizedCard = useSelector(selectCurrentUserCard());

  const isCardLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Card.GET_CARD_REQUEST));

  const isCardDeleting = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Card.DELETE_CARD_REQUEST));

  const isCardDeleted = useSelector((state: IStore) => selectAPIRequestStatus(state, Card.DELETE_CARD_REQUEST));

  useEffect(() => {
    if (cardId) dispatch(getCardRequest(cardId));
  }, [cardId, dispatch]);

  const [showModal, hideModal] = useModal(() => {
    let customModal;
    let formProps;
    let modalTitle;
    let modalChildren;
    let modalFooter;

    const resetModal = () => {
      hideModal();
      setModalContent(undefined);
    };

    const onSuccessToast = () => toast(<SuccessToastContainer title="Payment Method Added" message="You successfully added your payment method" />);

    const confirmRemoveCard = () => {
      if (tokenizedCard) dispatch(deleteCardRequest(tokenizedCard.id));
    };

    // There is going to be more options coming later
    switch (modalContent) {
      case 'AddPaymentMethod':
        customModal = true;
        modalChildren = <PaymentMethod onClose={resetModal} onSuccessToast={onSuccessToast} hasPaymentMethod={!!tokenizedCard} />;
        break;
      case 'RemovePaymentMethod':
        customModal = true;
        modalChildren = <PaymentMethodRemove onClose={resetModal} onConfirm={confirmRemoveCard} isLoading={isCardDeleting} />;
        break;
      default:
        break;
    }

    return (
      <ModalFactory customModal={customModal} formProps={formProps} title={modalTitle || ''} modalFooter={modalFooter} onClose={resetModal} isActive closeOnOutSideClick={false}>
        {modalChildren}
      </ModalFactory>
    );
  }, [tokenizedCard, isCardDeleting, modalContent]);

  useEffect(() => {
    if (isCardDeleted === RequestStatus.SUCCESS) {
      dispatch(getCurrentUserRequest()); // reload the user
      hideModal();
    }
  }, [isCardDeleted, hideModal, dispatch]);

  const processingAccountLabel = <em>•••• •••• •••• ••••</em>;
  const processingExpirationLabel = <em>••••</em>;
  const cardInfoComponent = (cardInfo: ICardDetails) => (
    <>
      <List data-cy="payment-method-settings-card">
        <ListItem label="Account Number" value={!cardInfo.network ? processingAccountLabel : <PaymentMethodAccountNumber cardType={cardInfo.network} cardNumber={cardInfo.last4} />} isLoading={isLoading} />
        <ListItem
          label="Expiration"
          value={
            !cardInfo.network ? (
              processingExpirationLabel
            ) : cardInfo.expDate ? (
              <div>
                {cardInfo.expDate && <CardExpirationStatus>{getExpirationStatus(cardInfo.expDate)}</CardExpirationStatus>}
                <CardExpirationDate>{cardInfo.expDate}</CardExpirationDate>
              </div>
            ) : (
              '--'
            )
          }
          isLoading={isLoading}
        />
        <ListItem label="Last Updated" value={<div>{formatDateForUI(cardInfo.updatedAt)}</div>} isLoading={isLoading} />
        <PaymentMethodDisclaimer>
          <Text size="small">
            {isLoading ? (
              <Skeleton count={2} />
            ) : (
              <>Funds transferred to this card account are typically available to spend in days. Your card issuer may impose transfer limits. For more information, please see your card issuer terms and conditions.</>
            )}
          </Text>
        </PaymentMethodDisclaimer>
      </List>

      <PaymentMethodActions data-cy="payment-method-settings-actions">
        <Button
          appearance="destructive"
          size="large"
          label="Remove"
          onClick={() => {
            setModalContent('RemovePaymentMethod');
            showModal();
          }}
          isLoading={isLoading}
        />
        <Button
          appearance="secondary"
          size="large"
          label="Change Card"
          onClick={() => {
            setModalContent('AddPaymentMethod');
            showModal();
          }}
          isLoading={isLoading}
        />
      </PaymentMethodActions>
    </>
  );

  const addPaymentMethodComponent = (
    <>
      {accountSetup ? (
        <ASVLayout>
          <img src={AccountSetupIllustration} alt="Set up your bank card illustration" />

          <Button
            appearance="primary"
            label="Add Payment Method"
            data-cy="add-payment-method-button"
            onClick={() => {
              setModalContent('AddPaymentMethod');
              showModal();
            }}
          />
          <Text size="xxsmall" style={{ color: theme.color.neutral[800] }} data-cy="account-setup-chime">
            {"If you are having trouble adding your card, or if you can't receive payments on your card, we recommend that you sign up for an account from "}
            <a style={{ color: theme.color.primary[500] }} target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.CHIME}>
              Chime
            </a>
            .
          </Text>
        </ASVLayout>
      ) : (
        <Content data-cy="payment-method-settings-content">
          <Text size="small">
            {"Add a payment method to start receiving payments. If you are having trouble adding your card, or if you can't receive payments on your card, we recommend that you sign up for an account from "}
            <a style={{ color: theme.color.primary[500] }} target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.CHIME}>
              Chime
            </a>
            .
          </Text>

          <Button
            appearance="primary"
            size="small"
            label="Add Payment Method"
            onClick={() => {
              setModalContent('AddPaymentMethod');
              showModal();
            }}
          />
        </Content>
      )}
    </>
  );

  const paymentMethodContent = tokenizedCard && cardId ? cardInfoComponent(tokenizedCard) : addPaymentMethodComponent;

  return (
    <ContentContainer>
      {!accountSetup && (
        <Header>
          <H3>Payment Method</H3>
        </Header>
      )}

      {isCardLoading ? <LoaderDots /> : paymentMethodContent}
    </ContentContainer>
  );
};

export default PaymentMethodForm;
